import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { notifyRendezVousCancelled } from '../../../lib/notifications';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface RendezVous {
  id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  statut: string;
  specialite: string;
  diagnostique?: string;
  medicaments?: string;
  traitement?: string;
  prochain_rdv?: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Prescription {
  id: string;
  medicaments: any;
  diagnostic?: string;
  duree_traitement?: string;
  instructions?: string;
  photo_url?: string;
  date_prescription: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface SuiviPatient {
  id: string;
  objet: string;
  message: string;
  diagnostique?: string;
  traitement?: string;
  notes_medicales?: string;
  date_creation: string;
  medecin_expediteur: {
    nom: string;
    prenom: string;
    specialite: string;
  };
  medecin_destinataire: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

export default function PatientRendezVousPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [loading, setLoading] = useState(false);
  const [patientInfo, setPatientInfo] = useState<any>(null);
  const [selectedRdv, setSelectedRdv] = useState<RendezVous | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [suivis, setSuivis] = useState<SuiviPatient[]>([]);
  const [showPrescriptionsModal, setShowPrescriptionsModal] = useState(false);
  const [showSuivisModal, setShowSuivisModal] = useState(false);
  const [showDossierModal, setShowDossierModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (role !== 'patient') {
      navigate('/', { replace: true });
      return;
    }

    const run = async () => {
      try {
        setLoading(true);
        await loadPatientInfo();
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [user, role, navigate]);

  const loadPatientInfo = async () => {
    if (!user) return;

    const { data } = await supabase
      .from('patients')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (data) {
      setPatientInfo(data);
      await Promise.all([
        loadRendezVous(data.id),
        loadPrescriptions(data.id),
        loadSuivis(data.id)
      ]);
    }
  };

  const loadRendezVous = async (patientId: string) => {
    try {
      const { data, error } = await supabase
        .from('rendez_vous')
        .select(`
          *,
          medecins (
            id,
            nom,
            prenom,
            specialite
          )
        `)
        .eq('patient_id', patientId)
        .order('date_rdv', { ascending: true });

      if (error) throw error;

      if (data) {
        console.log('📅 Rendez-vous chargés:', data);
        
        // Vérifier et mettre à jour les rendez-vous manqués
        const now = new Date();
        const rdvsToUpdate: string[] = [];

        data.forEach(rdv => {
          if (rdv.statut !== 'termine' && rdv.statut !== 'annule' && rdv.statut !== 'manque' && rdv.statut !== 'en_cours') {
            // Vérifier que les champs nécessaires existent
            if (!rdv.date_rdv || !rdv.heure_debut) {
              console.warn('⚠️ Rendez-vous avec données manquantes:', {
                id: rdv.id,
                date_rdv: rdv.date_rdv,
                heure_debut: rdv.heure_debut
              });
              return; // Ignorer ce rendez-vous
            }

            const [year, month, day] = rdv.date_rdv.split('-').map(Number);
            const [heureDebut, minuteDebut] = rdv.heure_debut.split(':').map(Number);
            
            // Créer une date avec l'heure de DÉBUT du rendez-vous
            const rdvStartDate = new Date(year, month - 1, day, heureDebut, minuteDebut);
            
            console.log('🔍 Vérification rendez-vous:', {
              id: rdv.id,
              date_rdv: rdv.date_rdv,
              heure_debut: rdv.heure_debut,
              rdvStartDate: rdvStartDate.toLocaleString(),
              now: now.toLocaleString(),
              isPast: rdvStartDate < now
            });

            // Si l'heure de DÉBUT est passée, marquer comme manqué
            if (rdvStartDate < now) {
              rdvsToUpdate.push(rdv.id);
            }
          }
        });

        console.log('📝 Rendez-vous à marquer comme manqués:', rdvsToUpdate);

        // Mettre à jour les rendez-vous manqués
        if (rdvsToUpdate.length > 0) {
          const { error: updateError } = await supabase
            .from('rendez_vous')
            .update({ statut: 'manque' })
            .in('id', rdvsToUpdate);

          if (updateError) {
            // Silencieux
          }

          // Recharger les données après mise à jour
          const { data: updatedData } = await supabase
            .from('rendez_vous')
            .select(`
              *,
              medecins (
                id,
                nom,
                prenom,
                specialite
              )
            `)
            .eq('patient_id', patientId)
            .order('date_rdv', { ascending: true });

          if (updatedData) {
            setRendezVous(updatedData);
          }
        } else {
          setRendezVous(data);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
    }
  };

  const loadPrescriptions = async (patientId: string) => {
    const { data, error } = await supabase
      .from('prescriptions')
      .select(`
        *,
        medecins(nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_prescription', { ascending: false });

    console.log('📋 Données prescriptions reçues:', data);
    console.log('❌ Erreur prescriptions:', error);

    if (error) {
      console.error('Erreur chargement prescriptions:', error);
    } else if (data) {
      console.log('✅ Prescriptions chargées:', data.length, 'prescriptions trouvées');
      setPrescriptions(data);
    }
  };

  const loadSuivis = async (patientId: string) => {
    const { data } = await supabase
      .from('suivis_patients')
      .select(`
        *,
        medecin_expediteur:medecins!medecin_expediteur_id(nom, prenom, specialite),
        medecin_destinataire:medecins!medecin_destinataire_id(nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_creation', { ascending: false });

    if (data) {
      setSuivis(data);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedRdv) return;

    const { error } = await supabase
      .from('rendez_vous')
      .update({ statut: 'annule' })
      .eq('id', selectedRdv.id);

    if (!error) {
      // ✅ NOUVEAU : Envoyer notifications au médecin ET au patient
      try {
        const dateFormatted = formatDate(selectedRdv.date_rdv);
        const patientName = `${patientInfo.prenom} ${patientInfo.nom}`;
        const medecinFullName = `Dr ${selectedRdv.medecins.nom} ${selectedRdv.medecins.prenom}`;

        // 1. Notification au MÉDECIN
        const { data: medecinData } = await supabase
          .from('medecins')
          .select('user_id, email, telephone')
          .eq('id', selectedRdv.medecins.id)
          .single();

        if (medecinData) {
          // Notification système
          await notifyRendezVousCancelled(
            medecinData.user_id,
            'medecin',
            dateFormatted,
            selectedRdv.heure_debut,
            'Annulé par le patient'
          );

          // Email/SMS au médecin
          if (medecinData.email) {
            await supabase.functions.invoke('send-email', {
              body: {
                to: medecinData.email,
                toName: medecinFullName,
                subject: '❌ Rendez-vous annulé',
                htmlContent: `
                  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                    <div style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                      <h1 style="color: white; margin: 0;">❌ Rendez-vous annulé</h1>
                    </div>
                    <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                      <h2 style="color: #d97706;">Bonjour ${medecinFullName},</h2>
                      <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                        Un patient a annulé son rendez-vous :
                      </p>
                      <div style="background: white; border-left: 4px solid #ef4444; padding: 20px; margin: 20px 0; border-radius: 5px;">
                        <p style="margin: 5px 0; color: #1f2937;"><strong>👤 Patient :</strong> ${patientName}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>📅 Date :</strong> ${dateFormatted}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>🕐 Heure :</strong> ${selectedRdv.heure_debut}</p>
                        <p style="margin: 5px 0; color: #1f2937;"><strong>🩺 Spécialité :</strong> ${selectedRdv.medecins.specialite}</p>
                      </div>
                      <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0; border-radius: 5px;">
                        <p style="color: #1e40af; margin: 0; font-size: 14px;">
                          ℹ️ Ce créneau est maintenant disponible pour d'autres patients.
                        </p>
                      </div>
                      <div style="text-align: center; margin: 30px 0;">
                        <a href="${window.location.origin}/medecin/rendez-vous" 
                           style="background: #3b82f6; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; display: inline-block;">
                          Voir mes rendez-vous
                        </a>
                      </div>
                    </div>
                  </div>
                `
              }
            });
            console.log('✅ Email annulation envoyé au médecin');
          } else if (medecinData.telephone) {
            await supabase.functions.invoke('send-sms', {
              body: {
                to: medecinData.telephone,
                message: `RDV annulé : ${patientName} a annulé son rendez-vous du ${dateFormatted} à ${selectedRdv.heure_debut}. Le créneau est maintenant disponible.`
              }
            });
            console.log('✅ SMS annulation envoyé au médecin');
          }
        }

        // 2. Confirmation au PATIENT
        if (patientInfo.email) {
          await supabase.functions.invoke('send-email', {
            body: {
              to: patientInfo.email,
              toName: patientName,
              subject: '✅ Annulation confirmée - Clinique Sainte-Famille',
              htmlContent: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <div style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
                    <h1 style="color: white; margin: 0;">✅ Annulation confirmée</h1>
                  </div>
                  <div style="background: #f9fafb; padding: 30px; border-radius: 0 0 10px 10px;">
                    <h2 style="color: #d97706;">Bonjour ${patientName},</h2>
                    <p style="color: #4b5563; font-size: 16px; line-height: 1.6;">
                      Votre rendez-vous a été annulé avec succès.
                    </p>
                    <div style="background: white; border-left: 4px solid #f59e0b; padding: 20px; margin: 20px 0; border-radius: 5px;">
                      <p style="margin: 5px 0; color: #1f2937;"><strong>👨‍⚕️ Médecin :</strong> ${medecinFullName}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>🩺 Spécialité :</strong> ${selectedRdv.medecins.specialite}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>📅 Date :</strong> ${dateFormatted}</p>
                      <p style="margin: 5px 0; color: #1f2937;"><strong>🕐 Heure :</strong> ${selectedRdv.heure_debut}</p>
                    </div>
                    <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0; border-radius: 5px;">
                      <p style="color: #1e40af; margin: 0; font-size: 14px;">
                        💡 Vous pouvez prendre un nouveau rendez-vous à tout moment.
                      </p>
                    </div>
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${window.location.origin}/rendez-vous" 
                         style="background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 25px; font-weight: bold; display: inline-block;">
                        Prendre un nouveau rendez-vous
                      </a>
                    </div>
                  </div>
                </div>
              `
            }
          });
          console.log('✅ Email confirmation annulation envoyé au patient');
        } else if (patientInfo.telephone) {
          await supabase.functions.invoke('send-sms', {
            body: {
              to: patientInfo.telephone,
              message: `Clinique Sainte-Famille : Votre RDV avec ${medecinFullName} du ${dateFormatted} à ${selectedRdv.heure_debut} a été annulé. Vous pouvez reprendre RDV à tout moment.`
            }
          });
          console.log('✅ SMS confirmation annulation envoyé au patient');
        }

      } catch (notifError) {
        console.error('⚠️ Erreur envoi notifications annulation:', notifError);
      }

      loadRendezVous(patientInfo.id);
      setShowModal(false);
      setSelectedRdv(null);
    }
  };

  const groupByStatus = (appointments: RendezVous[]) => {
    const upcoming = appointments.filter(rdv => 
      rdv.statut === 'en_attente' || rdv.statut === 'en_cours' || rdv.statut === 'confirme'
    );
    const past = appointments.filter(rdv => 
      rdv.statut === 'termine' || rdv.statut === 'annule' || rdv.statut === 'manque'
    );
    return { upcoming, past };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-50 text-yellow-600';
      case 'en_cours': return 'bg-blue-50 text-blue-600';
      case 'termine': return 'bg-green-50 text-green-600';
      case 'annule': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  const getStatusText = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'En attente';
      case 'en_cours': return 'En cours';
      case 'termine': return 'Terminé';
      case 'annule': return 'Annulé';
      default: return statut;
    }
  };

  const getStatusBadge = (statut: string) => {
    const badges: { [key: string]: { bg: string; text: string; label: string } } = {
      en_attente: { bg: 'bg-yellow-50', text: 'text-yellow-600', label: 'En attente' },
      confirme: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Confirmé' },
      termine: { bg: 'bg-green-50', text: 'text-green-600', label: 'Terminé' },
      annule: { bg: 'bg-red-50', text: 'text-red-600', label: 'Annulé' },
      manque: { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Manqué' },
    };

    const badge = badges[statut] || badges.en_attente;
    return (
      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getStatutBadge = (rdv: any) => {
    // Vérifier si le rendez-vous est passé
    const now = new Date();
    
    // Vérifier que les champs nécessaires existent
    if (!rdv.date_rdv || !rdv.heure_debut || !rdv.heure_fin) {
      // Si les données sont manquantes, afficher le statut normal
      switch (rdv.statut) {
        case 'en_attente':
          return (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              En attente
            </span>
          );
        case 'confirme':
          return (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Confirmé
            </span>
          );
        case 'annule':
          return (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
              Annulé
            </span>
          );
        case 'termine':
          return (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              Terminé
            </span>
          );
        default:
          return null;
      }
    }
    
    const [year, month, day] = rdv.date_rdv.split('-').map(Number);
    const [heureDebut, minuteDebut] = rdv.heure_debut.split(':').map(Number);
    const [heureFin, minuteFin] = rdv.heure_fin.split(':').map(Number);
    
    const rdvDateDebut = new Date(year, month - 1, day, heureDebut, minuteDebut);
    const rdvDateFin = new Date(year, month - 1, day, heureFin, minuteFin);
    
    const isPast = now > rdvDateFin;
    
    // Si le rendez-vous est passé et n'est pas terminé ou annulé, l'afficher comme manqué
    if (isPast && rdv.statut !== 'termine' && rdv.statut !== 'annule') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Manqué
        </span>
      );
    }

    // Sinon afficher le statut normal
    switch (rdv.statut) {
      case 'en_attente':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            En attente
          </span>
        );
      case 'confirme':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Confirmé
          </span>
        );
      case 'annule':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            Annulé
          </span>
        );
      case 'termine':
        return (
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            Terminé
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  const { upcoming, past } = groupByStatus(rendezVous);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <h1 className="text-2xl md:text-4xl font-bold text-clinic-blue-dark mb-1">Mes rendez-vous</h1>
                {patientInfo && (
                  <p className="text-gray-600 text-sm md:text-base">
                    {patientInfo.prenom} {patientInfo.nom}
                  </p>
                )}
              </div>
              <button
                onClick={() => navigate('/rendez-vous')}
                className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-sm md:text-base self-start sm:self-auto"
              >
                <i className="ri-add-line"></i>
                Nouveau RDV
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowDossierModal(true)}
                className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 bg-clinic-blue-dark text-white rounded-full hover:bg-clinic-blue transition-all font-medium whitespace-nowrap cursor-pointer text-xs md:text-sm"
              >
                <i className="ri-folder-user-line"></i>
                <span className="hidden sm:inline">Mon dossier médical</span>
                <span className="sm:hidden">Dossier</span>
              </button>
              <button
                onClick={() => setShowPrescriptionsModal(true)}
                className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-all font-medium whitespace-nowrap cursor-pointer text-xs md:text-sm"
              >
                <i className="ri-file-text-line"></i>
                <span className="hidden sm:inline">Prescriptions</span>
                <span className="sm:hidden">Prescr.</span>
                <span>({prescriptions.length})</span>
              </button>
              <button
                onClick={() => setShowSuivisModal(true)}
                className="flex items-center gap-1.5 px-3 md:px-4 py-1.5 md:py-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-all font-medium whitespace-nowrap cursor-pointer text-xs md:text-sm"
              >
                <i className="ri-mail-line"></i>
                <span className="hidden sm:inline">Suivis médicaux</span>
                <span className="sm:hidden">Suivis</span>
                <span>({suivis.length})</span>
              </button>
            </div>
          </div>

          {rendezVous.length === 0 ? (
            <div className="bg-white p-12 rounded-3xl shadow-xl border-2 border-clinic-blue/10 text-center">
              <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-line text-4xl text-clinic-blue"></i>
              </div>
              <h3 className="text-2xl font-bold text-clinic-blue-dark mb-2">Aucun rendez-vous</h3>
              <p className="text-gray-600 mb-6">Vous n'avez pas encore de rendez-vous programmés</p>
              <button
                onClick={() => navigate('/rendez-vous')}
                className="px-6 py-3 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Prendre un rendez-vous
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Rendez-vous à venir */}
              {upcoming.length > 0 && (
                <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
                  <h2 className="text-2xl font-bold text-clinic-blue-dark mb-6 flex items-center">
                    <i className="ri-calendar-event-line mr-3 text-clinic-green"></i>
                    Rendez-vous à venir ({upcoming.length})
                  </h2>

                  <div className="space-y-4">
                    {upcoming.map((rdv) => (
                      <div
                        key={rdv.id}
                        className="bg-gradient-to-r from-clinic-blue/5 to-transparent p-4 md:p-6 rounded-2xl border-2 border-clinic-blue/10 hover:border-clinic-green/30 transition-all"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 md:w-12 md:h-12 bg-clinic-blue rounded-xl flex items-center justify-center flex-shrink-0">
                                <i className="ri-calendar-check-line text-lg md:text-xl text-white"></i>
                              </div>
                              <div>
                                <p className="text-base md:text-xl font-bold text-clinic-blue-dark">
                                  {formatDate(rdv.date_rdv)}
                                </p>
                                <p className="text-sm md:text-lg text-gray-700">
                                  {rdv.heure_debut} - {rdv.heure_fin}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-1.5 text-sm">
                              <div className="flex items-center gap-2">
                                <i className="ri-user-line text-clinic-blue"></i>
                                <p className="text-gray-700">
                                  <strong>Médecin :</strong> Dr {rdv.medecins.nom} {rdv.medecins.prenom}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <i className="ri-stethoscope-line text-clinic-blue"></i>
                                <p className="text-gray-700">
                                  <strong>Spécialité :</strong> {rdv.medecins.specialite}
                                </p>
                              </div>
                            </div>

                            {rdv.statut === 'en_attente' && (
                              <div className="mt-3">
                                <button
                                  onClick={() => {
                                    setSelectedRdv(rdv);
                                    setShowModal(true);
                                  }}
                                  className="px-4 py-2 bg-red-500 text-white rounded-full text-xs md:text-sm font-medium hover:bg-red-600 transition-all whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-close-circle-line mr-1"></i>
                                  Annuler le rendez-vous
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="self-start">
                            <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold whitespace-nowrap ${getStatusColor(rdv.statut)}`}>
                              <i className="ri-checkbox-circle-line mr-1"></i>
                              {getStatusText(rdv.statut)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Historique */}
              {past.length > 0 && (
                <div className="bg-white p-4 md:p-8 rounded-3xl shadow-xl border-2 border-clinic-blue/10">
                  <h2 className="text-xl md:text-2xl font-bold text-clinic-blue-dark mb-4 md:mb-6 flex items-center">
                    <i className="ri-history-line mr-3 text-gray-500"></i>
                    Historique ({past.length})
                  </h2>

                  <div className="space-y-3 md:space-y-4">
                    {past.map((rdv) => (
                      <div
                        key={rdv.id}
                        className="bg-gray-50 p-4 md:p-6 rounded-2xl border-2 border-gray-100"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                <i className="ri-calendar-line text-lg md:text-xl text-gray-600"></i>
                              </div>
                              <div>
                                <p className="text-base md:text-xl font-bold text-gray-700">
                                  {formatDate(rdv.date_rdv)}
                                </p>
                                <p className="text-sm md:text-lg text-gray-600">
                                  {rdv.heure_debut} - {rdv.heure_fin}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-1.5 text-sm">
                              <div className="flex items-center gap-2">
                                <i className="ri-user-line text-gray-600"></i>
                                <p className="text-gray-700">
                                  <strong>Médecin :</strong> Dr {rdv.medecins.nom} {rdv.medecins.prenom}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <i className="ri-stethoscope-line text-gray-600"></i>
                                <p className="text-gray-700">
                                  <strong>Spécialité :</strong> {rdv.medecins.specialite}
                                </p>
                              </div>

                              {rdv.statut === 'termine' && rdv.diagnostique && (
                                <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200">
                                  <h4 className="font-semibold text-clinic-blue-dark mb-2 flex items-center">
                                    <i className="ri-file-text-line mr-2"></i>
                                    Compte-rendu de consultation
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <div>
                                      <strong className="text-gray-700">Diagnostic :</strong>
                                      <p className="text-gray-600">{rdv.diagnostique}</p>
                                    </div>
                                    {rdv.medicaments && (
                                      <div>
                                        <strong className="text-gray-700">Médicaments prescrits :</strong>
                                        <p className="text-gray-600">{rdv.medicaments}</p>
                                      </div>
                                    )}
                                    {rdv.traitement && (
                                      <div>
                                        <strong className="text-gray-700">Traitement recommandé :</strong>
                                        <p className="text-gray-600">{rdv.traitement}</p>
                                      </div>
                                    )}
                                    {rdv.prochain_rdv && (
                                      <div>
                                        <strong className="text-gray-700">Prochain rendez-vous :</strong>
                                        <p className="text-gray-600">{rdv.prochain_rdv}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <span className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${getStatusColor(rdv.statut)}`}>
                              <i className="ri-checkbox-circle-line mr-1"></i>
                              {getStatusText(rdv.statut)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal d'annulation */}
      {showModal && selectedRdv && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark">Annuler le rendez-vous</h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="mb-6 p-4 bg-clinic-blue/5 rounded-2xl">
              <p className="text-clinic-blue-dark">
                <strong>Date :</strong> {formatDate(selectedRdv.date_rdv)}
              </p>
              <p className="text-gray-600">
                <strong>Heure :</strong> {selectedRdv.heure_debut} - {selectedRdv.heure_fin}
              </p>
              <p className="text-gray-600">
                <strong>Médecin :</strong> Dr {selectedRdv.medecins.nom} {selectedRdv.medecins.prenom}
              </p>
            </div>

            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir annuler ce rendez-vous ?</p>

            <div className="flex gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                Non, garder
              </button>
              <button
                onClick={handleCancelAppointment}
                className="flex-1 px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-circle-line mr-2"></i>
                Oui, annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dossier Medical */}
      {showDossierModal && patientInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark flex items-center">
                <i className="ri-folder-user-line mr-3 text-clinic-blue"></i>
                Mon dossier médical
              </h3>
              <button
                onClick={() => setShowDossierModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Informations personnelles */}
              <div className="bg-gradient-to-r from-clinic-blue/5 to-white p-6 rounded-2xl border-2 border-clinic-blue/10">
                <h4 className="text-xl font-bold text-clinic-blue-dark mb-4 flex items-center">
                  <i className="ri-user-line mr-2"></i>
                  Informations personnelles
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Nom complet</label>
                    <p className="text-gray-900 font-semibold">{patientInfo.prenom} {patientInfo.nom}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Date de naissance</label>
                    <p className="text-gray-900">{formatDate(patientInfo.date_naissance)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Téléphone</label>
                    <p className="text-gray-900">{patientInfo.telephone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <p className="text-gray-900">{patientInfo.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Adresse</label>
                    <p className="text-gray-900">{patientInfo.adresse}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Groupe sanguin</label>
                    <p className="text-gray-900">{patientInfo.groupe_sanguin || 'Non renseigné'}</p>
                  </div>
                </div>
              </div>

              {/* Informations médicales */}
              {(patientInfo.allergies || patientInfo.antecedents_medicaux || patientInfo.medicaments_actuels) && (
                <div className="bg-gradient-to-r from-red-50 to-white p-6 rounded-2xl border-2 border-red-100">
                  <h4 className="text-xl font-bold text-red-700 mb-4 flex items-center">
                    <i className="ri-heart-pulse-line mr-2"></i>
                    Informations médicales importantes
                  </h4>
                  <div className="space-y-4">
                    {patientInfo.allergies && (
                      <div>
                        <label className="block text-sm font-medium text-red-600 mb-1">Allergies</label>
                        <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                          <p className="text-red-900">{patientInfo.allergies}</p>
                        </div>
                      </div>
                    )}
                    {patientInfo.antecedents_medicaux && (
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">Antécédents médicaux</label>
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <p className="text-orange-900">{patientInfo.antecedents_medicaux}</p>
                        </div>
                      </div>
                    )}
                    {patientInfo.medicaments_actuels && (
                      <div>
                        <label className="block text-sm font-medium text-blue-600 mb-1">Médicaments actuels</label>
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <p className="text-blue-900">{patientInfo.medicaments_actuels}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Résumé des consultations récentes */}
              <div className="bg-gradient-to-r from-green-50 to-white p-6 rounded-2xl border-2 border-green-100">
                <h4 className="text-xl font-bold text-green-700 mb-4 flex items-center">
                  <i className="ri-file-text-line mr-2"></i>
                  Résumé des consultations récentes
                </h4>
                <div className="space-y-3">
                  {past.slice(0, 5).map((rdv) => (
                    <div key={rdv.id} className="bg-white p-4 rounded-lg border border-green-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-green-900">
                            {formatDate(rdv.date_rdv)} - Dr {rdv.medecins.nom} {rdv.medecins.prenom}
                          </p>
                          <p className="text-sm text-green-700">{rdv.medecins.specialite}</p>
                        </div>
                      </div>
                      {rdv.diagnostique && (
                        <div className="mt-2 text-sm">
                          <p className="text-gray-700"><strong>Diagnostic :</strong> {rdv.diagnostique}</p>
                          {rdv.traitement && (
                            <p className="text-gray-700 mt-1"><strong>Traitement :</strong> {rdv.traitement}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {past.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Aucune consultation antérieure</p>
                  )}
                </div>
              </div>

              {/* Statistiques */}
              <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl border-2 border-purple-100">
                <h4 className="text-xl font-bold text-purple-700 mb-4 flex items-center">
                  <i className="ri-bar-chart-line mr-2"></i>
                  Statistiques de santé
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-purple-200 text-center">
                    <div className="text-2xl font-bold text-purple-700">{rendezVous.length}</div>
                    <div className="text-sm text-purple-600">Total consultations</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-purple-200 text-center">
                    <div className="text-2xl font-bold text-green-600">{prescriptions.length}</div>
                    <div className="text-sm text-green-600">Prescriptions</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-purple-200 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{suivis.length}</div>
                    <div className="text-sm text-indigo-600">Suivis médicaux</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Prescriptions */}
      {showPrescriptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark flex items-center">
                <i className="ri-file-text-line mr-3 text-green-600"></i>
                Mes prescriptions ({prescriptions.length})
              </h3>
              <button
                onClick={() => setShowPrescriptionsModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="space-y-6">
              {prescriptions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-text-line text-3xl text-green-600"></i>
                  </div>
                  <p className="text-gray-500 text-lg">Aucune prescription trouvée</p>
                </div>
              ) : (
                prescriptions.map((prescription) => (
                  <div key={prescription.id} className="border-2 border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                          <i className="ri-capsule-line text-2xl text-green-600"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-clinic-blue-dark">Prescription</h3>
                          <p className="text-sm text-gray-600">
                            {prescription.date_prescription ? new Date(prescription.date_prescription).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            }) : 'Date non disponible'}
                          </p>
                        </div>
                      </div>
                      {prescription.medecins && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-clinic-blue-dark">
                            Dr {prescription.medecins.nom} {prescription.medecins.prenom}
                          </p>
                          <p className="text-xs text-gray-600">{prescription.medecins.specialite}</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-xl p-4 space-y-3">
                      <div>
                        <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                          <i className="ri-medicine-bottle-line mr-2"></i>
                          Médicaments prescrits
                        </h5>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          {Array.isArray(prescription.medicaments) ? (
                            <ul className="list-disc list-inside space-y-1">
                              {prescription.medicaments.map((med: any, index: number) => (
                                <li key={index} className="text-gray-700">
                                  {typeof med === 'object' && med.nom ? med.nom : med}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-700">{prescription.medicaments}</p>
                          )}
                        </div>
                      </div>

                      {prescription.diagnostic && (
                        <div>
                          <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                            <i className="ri-stethoscope-line mr-2"></i>
                            Diagnostic
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{prescription.diagnostic}</p>
                        </div>
                      )}

                      {prescription.duree_traitement && (
                        <div>
                          <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                            <i className="ri-calendar-line mr-2"></i>
                            Durée du traitement
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{prescription.duree_traitement}</p>
                        </div>
                      )}

                      {prescription.instructions && (
                        <div>
                          <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                            <i className="ri-information-line mr-2"></i>
                            Instructions
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{prescription.instructions}</p>
                        </div>
                      )}

                      {prescription.photo_url && (
                        <div>
                          <h5 className="font-semibold text-green-700 mb-2 flex items-center">
                            <i className="ri-image-line mr-2"></i>
                            Ordonnance (photo)
                          </h5>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <img 
                              src={prescription.photo_url} 
                              alt="Ordonnance" 
                              className="max-w-full h-auto rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                              onClick={() => window.open(prescription.photo_url, '_blank')}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Suivis Médicaux */}
      {showSuivisModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark flex items-center">
                <i className="ri-mail-line mr-3 text-indigo-600"></i>
                Mes suivis médicaux ({suivis.length})
              </h3>
              <button
                onClick={() => setShowSuivisModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="space-y-6">
              {suivis.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-line text-3xl text-indigo-600"></i>
                  </div>
                  <p className="text-gray-500 text-lg">Aucun suivi médical trouvé</p>
                </div>
              ) : (
                suivis.map((suivi) => (
                  <div key={suivi.id} className="bg-gradient-to-r from-indigo-50 to-white p-6 rounded-2xl border-2 border-indigo-100">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                          <i className="ri-mail-fill text-xl text-white"></i>
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-gray-900">{suivi.objet}</h4>
                          <div className="text-sm text-gray-600 mt-1">
                            <p>
                              <strong>De :</strong> Dr. {suivi.medecin_expediteur.nom} {suivi.medecin_expediteur.prenom} 
                              <span className="text-indigo-600"> ({suivi.medecin_expediteur.specialite})</span>
                            </p>
                            <p>
                              <strong>À :</strong> Dr. {suivi.medecin_destinataire.nom} {suivi.medecin_destinataire.prenom}
                              <span className="text-indigo-600"> ({suivi.medecin_destinataire.specialite})</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full border">
                        {formatDate(suivi.date_creation)}
                      </span>
                    </div>

                    <div className="bg-white rounded-xl p-4 space-y-3">
                      <div>
                        <h5 className="font-semibold text-indigo-700 mb-2 flex items-center">
                          <i className="ri-message-2-line mr-2"></i>
                          Message
                        </h5>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{suivi.message}</p>
                      </div>

                      {suivi.diagnostique && (
                        <div>
                          <h5 className="font-semibold text-indigo-700 mb-2 flex items-center">
                            <i className="ri-stethoscope-line mr-2"></i>
                            Diagnostic
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{suivi.diagnostique}</p>
                        </div>
                      )}

                      {suivi.traitement && (
                        <div>
                          <h5 className="font-semibold text-indigo-700 mb-2 flex items-center">
                            <i className="ri-heart-pulse-line mr-2"></i>
                            Traitement
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{suivi.traitement}</p>
                        </div>
                      )}

                      {suivi.notes_medicales && (
                        <div>
                          <h5 className="font-semibold text-indigo-700 mb-2 flex items-center">
                            <i className="ri-clipboard-line mr-2"></i>
                            Notes médicales
                          </h5>
                          <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{suivi.notes_medicales}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
